import { avoidCalendarDaySentinel, defaultDayTimeZone, farmDayKey } from './farmTime'

export type NormalizedDateResult = {
  ok: true
  isoDate: string // YYYY-MM-DD
  isoZ: string // ISO 8601 timestamp (UTC Z)
} | {
  ok: false
  reason: string
}

const pad2 = (value: number): string => String(value).padStart(2, '0')

const toDateTimeLocalValueFromLocalDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  const hour = date.getHours()
  const minute = date.getMinutes()
  return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`
}

const parseDateTimeLocalValue = (value: string): Date | null => {
  const trimmed = value.trim()
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])

  const isoDate = buildIsoDate(year, month, day)
  if (!isoDate) return null
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null

  const d = new Date(year, month - 1, day, hour, minute, 0, 0)
  if (Number.isNaN(d.getTime())) return null
  return d
}

const buildIsoDate = (year: number, month: number, day: number): string | null => {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
  if (year < 1900 || year > 3000) return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null

  const isoDate = `${year}-${pad2(month)}-${pad2(day)}`
  // Ensure it is a real calendar day (e.g. not 2026-02-31)
  const d = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return null
  if (d.toISOString().slice(0, 10) !== isoDate) return null
  return isoDate
}

const extractIsoDatePrefix = (value: string): string | null => {
  const match = value.match(/^\s*(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  const isoDate = buildIsoDate(Number(match[1]), Number(match[2]), Number(match[3]))
  return isoDate
}

const parseSlashDate = (value: string): string | null => {
  // Supports:
  // - MM/DD/YYYY
  // - DD/MM/YYYY (only when unambiguous, e.g. 17/02/2026)
  // - YYYY/MM/DD
  const trimmed = value.trim()

  const ymd = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/)
  if (ymd) {
    return buildIsoDate(Number(ymd[1]), Number(ymd[2]), Number(ymd[3]))
  }

  const mdyOrDmy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (mdyOrDmy) {
    const a = Number(mdyOrDmy[1])
    const b = Number(mdyOrDmy[2])
    const y = Number(mdyOrDmy[3])

    // Unambiguous: one side > 12.
    if (a > 12 && b <= 12) {
      return buildIsoDate(y, b, a) // DD/MM/YYYY
    }
    if (b > 12 && a <= 12) {
      return buildIsoDate(y, a, b) // MM/DD/YYYY
    }

    // Ambiguous (e.g. 02/03/2026). Default to DD/MM/YYYY for more intuitive UX in dd/mm locales.
    return buildIsoDate(y, b, a)
  }

  return null
}

const parseSlashDateTime = (value: string): { isoDate: string; isoZ: string } | null => {
  // Supports:
  // - DD/MM/YYYY HH:mm[:ss]
  // - MM/DD/YYYY HH:mm[:ss]
  // - YYYY/MM/DD HH:mm[:ss]
  const trimmed = value.trim()

  const ymd = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (ymd) {
    const isoDate = buildIsoDate(Number(ymd[1]), Number(ymd[2]), Number(ymd[3]))
    if (!isoDate) return null
    const hour = Number(ymd[4])
    const minute = Number(ymd[5])
    const second = ymd[6] ? Number(ymd[6]) : 0
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59 || second < 0 || second > 59) return null
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]), hour, minute, second)
    if (Number.isNaN(d.getTime())) return null
    return { isoDate, isoZ: avoidCalendarDaySentinel(d.toISOString()) }
  }

  const mdyOrDmy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (mdyOrDmy) {
    const a = Number(mdyOrDmy[1])
    const b = Number(mdyOrDmy[2])
    const y = Number(mdyOrDmy[3])
    const hour = Number(mdyOrDmy[4])
    const minute = Number(mdyOrDmy[5])
    const second = mdyOrDmy[6] ? Number(mdyOrDmy[6]) : 0
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59 || second < 0 || second > 59) return null

    // Unambiguous: one side > 12.
    let month = a
    let day = b
    if (a > 12 && b <= 12) {
      day = a
      month = b
    } else if (b > 12 && a <= 12) {
      day = b
      month = a
    } else {
      // Ambiguous: default to DD/MM for more intuitive UX in dd/mm locales.
      day = a
      month = b
    }

    const isoDate = buildIsoDate(y, month, day)
    if (!isoDate) return null
    const d = new Date(y, month - 1, day, hour, minute, second)
    if (Number.isNaN(d.getTime())) return null
    return { isoDate, isoZ: avoidCalendarDaySentinel(d.toISOString()) }
  }

  return null
}

export const normalizeToIsoZMidnight = (raw: string | null | undefined): NormalizedDateResult => {
  const value = typeof raw === 'string' ? raw.trim() : ''
  if (!value) {
    return { ok: false, reason: 'empty' }
  }

  // If already ISO Z (with optional milliseconds), keep it.
  // Examples:
  // - 2026-02-17T00:00:00Z
  // - 2026-02-17T00:00:00.000Z
  const isoZMatch = value.match(/^\s*(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.(\d{3}))?Z\s*$/)
  if (isoZMatch) {
    const isoDate = buildIsoDate(Number(isoZMatch[1].slice(0, 4)), Number(isoZMatch[1].slice(5, 7)), Number(isoZMatch[1].slice(8, 10)))
    if (!isoDate) return { ok: false, reason: 'unrecognized' }
    const ms = isoZMatch[3]
    const suffix = ms ? `.${ms}` : ''
    return {
      ok: true,
      isoDate,
      isoZ: `${isoDate}T${isoZMatch[2]}${suffix}Z`
    }
  }

  // Fast path: anything starting with YYYY-MM-DD (covers ISO date, ISO datetime, etc.)
  const isoDatePrefix = extractIsoDatePrefix(value)
  if (isoDatePrefix) {
    // If the raw string had a time component but not Z, parse it to preserve time.
    // Otherwise default to midnight.
    if (/^\s*\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      const parsed = new Date(value)
      if (!Number.isNaN(parsed.getTime())) {
        return { ok: true, isoDate: isoDatePrefix, isoZ: avoidCalendarDaySentinel(parsed.toISOString()) }
      }
    }

    return { ok: true, isoDate: isoDatePrefix, isoZ: `${isoDatePrefix}T00:00:00Z` }
  }

  const slashDateTime = parseSlashDateTime(value)
  if (slashDateTime) {
    return { ok: true, isoDate: slashDateTime.isoDate, isoZ: slashDateTime.isoZ }
  }

  const slash = parseSlashDate(value)
  if (slash) {
    return { ok: true, isoDate: slash, isoZ: `${slash}T00:00:00Z` }
  }

  // Last resort: try Date.parse for other formats.
  const parsed = new Date(value)
  if (!Number.isNaN(parsed.getTime())) {
    const isoDate = parsed.toISOString().slice(0, 10)
    return { ok: true, isoDate, isoZ: parsed.toISOString() }
  }

  return { ok: false, reason: 'unrecognized' }
}

export const toDateInputValue = (raw: string | null | undefined): string => {
  if (typeof raw !== 'string') return ''
  const normalized = normalizeToIsoZMidnight(raw)
  return normalized.ok ? normalized.isoDate : raw
}

// Reuse the same datetime-local mapping approach as Tasks screen.
// Note: datetime-local has no timezone; we round-trip through Date to store as ISO Z.
export const toInputDate = (iso: string | undefined): string => {
  if (!iso) return ''

  // If it's already a datetime-local value, keep it.
  const trimmed = iso.trim()
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed
  }

  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) return ''

  // Legacy / date-only convention: a lot of MVP tables historically stored
  // "YYYY-MM-DDT00:00:00Z" to represent the *day*, not an actual instant.
  // If we convert that to local time, it can shift to the previous day.
  // Preserve UX by keeping it anchored to the same calendar day.
  const utcMidnightMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.000)?Z$/)
  if (utcMidnightMatch) {
    return `${utcMidnightMatch[1]}T00:00`
  }

  return toDateTimeLocalValueFromLocalDate(date)
}

export const fromInputDate = (value: string): string | undefined => {
  if (!value) return undefined

  const parsedLocal = parseDateTimeLocalValue(value)
  if (parsedLocal) return avoidCalendarDaySentinel(parsedLocal.toISOString())

  // Fallback: let Date parse other inputs (e.g. full ISO strings)
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toISOString()
}

export const isValidTableDate = (raw: string): boolean => {
  if (!raw || raw.trim() === '') return true
  return normalizeToIsoZMidnight(raw).ok
}

/**
 * v2.1 finding #49 — render a stored date/time for human display while
 * preserving the calendar-day-sentinel convention that `toInputDate` uses
 * for the `<input type="datetime-local">` cell on the Database screen.
 *
 * `ProductionRecord.date` has two storage shapes:
 *  - Real instant — `"YYYY-MM-DDTHH:MM:SS.sssZ"` with a non-midnight time
 *    (e.g. Sampling-saved rows where the operator picked a wall-clock time).
 *  - Calendar-day sentinel — `"YYYY-MM-DDT00:00:00Z"` produced by the
 *    "Add Row" default and by CSV imports of date-only columns. This is
 *    NOT an actual UTC midnight instant; it is a flag meaning "this row
 *    represents the day YYYY-MM-DD with no recorded time-of-day."
 *
 * Naively calling `toLocaleString()` on the sentinel TZ-shifts UTC midnight
 * to the previous local day's evening (e.g. `2026-06-26T00:00:00Z` becomes
 * `"6/25/2026, 6:00:00 PM"` in UTC-6), so the Database (which honors the
 * sentinel via `toInputDate`) and the Sampling/Biomass screen (which used
 * raw `toLocaleString`) end up displaying different calendar days for the
 * same row. This helper mirrors the Database UX: sentinel → date-only,
 * real instant → full local timestamp.
 */
export const formatStoredDateTimeAsLocal = (raw: string | null | undefined, timeZone?: string): string => {
  // Findings #85: an instant is shown in the farm's zone (the record's farm
  // when the caller passes it, else the farms' shared zone), not the viewer's.
  const zone = timeZone ?? defaultDayTimeZone()
  if (typeof raw !== 'string' || !raw.trim()) return ''
  const trimmed = raw.trim()
  const normalized = normalizeToIsoZMidnight(trimmed)

  if (normalized.ok) {
    const utcMidnightMatch = normalized.isoZ.match(/^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/)
    if (utcMidnightMatch) {
      const year = Number(utcMidnightMatch[1])
      const month = Number(utcMidnightMatch[2])
      const day = Number(utcMidnightMatch[3])
      const localDay = new Date(year, month - 1, day)
      if (!Number.isNaN(localDay.getTime())) {
        return localDay.toLocaleDateString()
      }
    }

    const parsed = new Date(normalized.isoZ)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString(undefined, { timeZone: zone })
    }
  }

  const parsed = new Date(trimmed)
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString(undefined, { timeZone: zone })
  }

  return trimmed
}

// Back-compat alias: older call sites read "Midnight" in the name.
// This now means "normalize to ISO Z" (preserving time when present),
// while date-only inputs still become midnight.
export const normalizeToIsoZ = normalizeToIsoZMidnight

/**
 * Resolve `raw` to a local-calendar-day key (`YYYY-MM-DD`) using the user's
 * current time zone. This is the value users actually see on the page, so
 * it is the right key for grouping signals that span two storage shapes:
 *
 * - `ProductionRecord.date` is typically stored as a "datetime-local" string
 *   without a `Z` (e.g. `"2026-04-13T19:36"`), already in local time. Legacy
 *   rows may instead store ISO Z midnight (`"2026-04-13T00:00:00Z"`) as a
 *   pure date sentinel — that prefix is treated as the literal calendar day,
 *   matching the convention used by `toInputDate` for round-trip parity.
 * - `MortalityLog.recordedAt` is stored as an ISO Z timestamp with a real
 *   wall-clock time (e.g. `"2026-04-14T00:36:00.000Z"`), and is converted
 *   into the user's local zone before extracting the day.
 *
 * Mixing those two in a UTC-prefix bucketing (`normalizeToIsoZ().isoDate`)
 * causes evening rows to land in a *different* calendar day than their
 * own mortality event — see [docs/release/v2.1/findings.md](../../docs/release/v2.1/findings.md)
 * row #7 for the v2.1 evidence (the `(from 1 events)` ghost row).
 *
 * Returns `''` when `raw` cannot be parsed.
 */
export const toLocalCalendarDayKey = (raw: string | null | undefined, timeZone?: string): string => {
  if (typeof raw !== 'string') return ''
  const trimmed = raw.trim()
  if (!trimmed) return ''

  // ISO Z midnight ("YYYY-MM-DDT00:00:00Z" or ".000Z") — legacy "date-only"
  // sentinel. Take the prefix as the literal calendar day to match the
  // round-trip convention `toInputDate` uses.
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000)?Z$/.test(trimmed)) {
    return trimmed.slice(0, 10)
  }

  // Naive ISO local datetime ("YYYY-MM-DDTHH:MM[:SS][.mmm]" with NO Z and no
  // explicit offset) — the prefix is already the local day.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?$/.test(trimmed)) {
    return trimmed.slice(0, 10)
  }

  // Date-only ("YYYY-MM-DD") — local day equals the literal.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed
  }

  // Anything else (ISO Z with a real time, ISO with offset, slash formats,
  // etc.) is an instant: its day is the FARM's day (findings #85) — the
  // record's farm zone when the caller knows it, else the farms' shared zone
  // (`defaultDayTimeZone`), else the browser's. Using the viewer's browser
  // zone put evening crew records on a different day for an office in
  // another time zone.
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return ''
  return farmDayKey(parsed, timeZone ?? defaultDayTimeZone()) ?? ''
}
